import { useState } from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { invoiceTypeLabel } from '../lib/invoice-label';
import { invoiceVerbFor } from '../lib/invoice-actions';
import { CancelInvoiceDialog } from './cancel-invoice-dialog';
import { ClearInvoiceDialog } from './clear-invoice-dialog';

import type { InvoiceRow } from '../types';

export interface DiscardInvoiceButtonProps {
  readonly invoice: InvoiceRow;
  readonly onDiscarded?: (() => void) | undefined;
  readonly compact?: boolean | undefined;
  readonly iconOnly?: boolean | undefined;
}

// The consolidated Discard verb over cancel + delete. Routing follows the
// action model: a Draft cancels (Draft→Cancelled keeps an auditable record);
// the soft delete remains the route for never-issued rows past Draft
// (imported data), and it needs the owning order for cache invalidation.
// Voiding is deliberately NOT part of Discard — an issued document keeps its
// legal number as a gap.
export function DiscardInvoiceButton(props: DiscardInvoiceButtonProps) {
  const { invoice, onDiscarded, compact, iconOnly } = props;
  const [open, setOpen] = useState(false);
  const verb = invoiceVerbFor(invoice, 'discard');
  if (verb === null) return null;
  if (verb.routesTo === 'delete' && invoice.order_id === null) return null;
  return (
    <>
      <DiscardTrigger
        invoice={invoice}
        compact={compact}
        iconOnly={iconOnly}
        onOpen={() => {
          setOpen(true);
        }}
      />
      {verb.routesTo === 'cancel' ? (
        <CancelInvoiceDialog
          open={open}
          onOpenChange={setOpen}
          invoice={invoice}
          onCancelled={onDiscarded}
        />
      ) : (
        <DiscardDeleteDialog
          invoice={invoice}
          open={open}
          onOpenChange={setOpen}
          onCleared={onDiscarded ?? noop}
        />
      )}
    </>
  );
}

function DiscardDeleteDialog({
  invoice,
  open,
  onOpenChange,
  onCleared,
}: Readonly<{
  invoice: InvoiceRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCleared: () => void;
}>) {
  const blOrderIdProp = invoice.bl_order_id !== null ? { blOrderId: invoice.bl_order_id } : {};
  return (
    <ClearInvoiceDialog
      open={open}
      onOpenChange={onOpenChange}
      invoiceId={invoice.id}
      orderId={invoice.order_id ?? ''}
      {...blOrderIdProp}
      invoiceLabel={invoiceTypeLabel(invoice)}
      onCleared={onCleared}
    />
  );
}

function DiscardTrigger({
  invoice,
  compact,
  iconOnly,
  onOpen,
}: Readonly<{
  invoice: InvoiceRow;
  compact: boolean | undefined;
  iconOnly: boolean | undefined;
  onOpen: () => void;
}>) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={buttonClassName(compact, iconOnly)}
      aria-label={`Discard ${invoice.invoice_number ?? invoice.id}`}
      data-testid={`invoice-${invoice.id}-discard`}
      onClick={onOpen}
    >
      <Trash2 className="size-4" />
      {iconOnly === true ? null : 'Discard'}
    </Button>
  );
}

function buttonClassName(compact: boolean | undefined, iconOnly: boolean | undefined): string {
  const destructive = 'text-destructive hover:bg-destructive/10 hover:text-destructive';
  if (compact === true) return `${destructive} h-7 gap-1 px-3 text-xs [&_svg]:size-3`;
  return iconOnly === true ? destructive : `${destructive} gap-1`;
}

function noop(): void {
  /* no-op */
}
